/**
 * @fileoverview Type definitions for the tracking system using JSDoc.
 */

/**
 * @typedef {Object} TrackingEvent
 * @property {string} name - The name of the event to track
 * @property {Object} [params] - Optional parameters for the event
 */

/**
 * @typedef {Object} StandardPageViewParams
 * @property {string} page_name - The name of the page viewed
 * @property {string} [page_type] - Type of page (e.g., 'profile', 'dashboard')
 * @property {string} [referrer] - Where the user came from
 */

/**
 * @typedef {Object} StandardUserActionParams
 * @property {string} action_type - Type of action performed
 * @property {string} [action_source] - Where the action was triggered from
 * @property {'success'|'error'|'pending'} [status] - Status of the action
 */

/**
 * @typedef {Object} StandardFormParams
 * @property {string} form_name - The name of the form
 * @property {string} [form_type] - Type of form
 * @property {'success'|'error'|'validation_error'} [status] - Status of form submission
 */

/**
 * @typedef {Object} UserProperties
 * @property {string} [user_id] - User's unique identifier
 * @property {string} [organization_id] - User's organization ID
 * @property {string} [organization_name] - User's organization name
 * @property {string} [user_role] - User's role in the application
 * @property {string} [user_email] - User's email address
 */

/**
 * @typedef {'google_analytics'|'posthog'} TrackingServiceName
 */

/**
 * @typedef {Object} TrackerDebugOptions
 * @property {boolean} enabled - Whether debug mode is enabled
 * @property {boolean} [logInitialization=true] - Whether to log service initialization
 * @property {boolean} [logEvents=true] - Whether to log tracked events
 */

/**
 * Common event names based on previous tracking system
 * @enum {string}
 */
export const EventNames = {
  // Page/Route views
  PAGE_VIEWED: "page_viewed",

  // Authentication events
  LOGIN_INTENT: "login_intent",
  REGISTRATION_INTENT: "registration_intent",
  REGISTRATION_STARTED: "registration_started",
  REGISTRATION_COMPLETED: "registration_completed",

  // Invitations
  INVITATION_DIRECT_VIEWED: "invitation_direct_viewed",
  INVITATION_DIRECT_JOIN_INTENT: "invitation_direct_join_intent",
  INVITATION_CODE_ENTERED: "invitation_code_entered",
  INVITATION_CODE_SUCCESS: "invitation_code_success",
  INVITATION_CODE_FAILED: "invitation_code_failed",
  INVITATION_ORG_LOOKUP_FOUND: "invitation_org_lookup_found",
  INVITATION_ORG_LOOKUP_NOTFOUND: "invitation_org_lookup_notfound",
  INVITATION_ORG_LOOKUP_JOIN_INTENT: "invitation_org_lookup_join_intent",

  // Program related
  PROGRAM_INTERACTION: "program_interaction",
  PROGRAM_NEXT_PHASE_INTENT: "program_next_phase_intent",
  PROGRAM_NEXT_PHASE_GRANTED: "program_next_phase_granted",
  PROGRAM_INVITE_SENT: "program_invite_sent",
  PROGRAM_INVITE_SKIPPED: "program_invite_skipped",

  // Quiz/Questions
  QUIZ_START: "quiz_start",
  QUIZ_COMPLETE: "quiz_complete",
  QUESTION_ANSWERED: "question_answered",

  // Portal specific
  PORTAL_ONBOARDING_STEP_VIEWED: "portal_onboarding_step_viewed",
  PORTAL_PROFILE_UPDATED: "portal_profile_updated",
  PORTAL_UPLOADED_LOGO: "portal_uploaded_logo",
  PORTAL_FILE_UPLOADED: "portal_file_uploaded",
  PORTAL_INVITE_SENT: "portal_invite_sent",
  PORTAL_ACCOUNT_UPDATED: "portal_account_updated",
  PORTAL_POST_VIEWED: "portal_post_viewed",
};

// Export nothing by default, as this file only provides type definitions
export default {};
